import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  myTeamAbsenceSummaryService,
  type MyTeamAbsenceSummary,
} from '@/api/domains/attendance/myTeam';
import { useAbortController } from '@/hooks/ui/useAbortController';
import { useUI } from '@/hooks/ui/useUI';


export interface UseMyTeamAbsenceSummaryResult {
  summary: MyTeamAbsenceSummary | null;
  isLoading: boolean;
}

// Data hook for the "Mi equipo" dashboard "Situación de asistencia" section — "Ausentes"
// card (ASIS-5406 / REXASIS-005). Recalculates whenever `companyIds`/`employeeIds` change —
// i.e. whenever the Empresa/Colaborador filters of the "Mi equipo" dashboard change.
// Callers should memoize the arrays they pass in (they derive from stable filter state),
// so this effect only re-runs on an actual filter change, not on every render.
export function useMyTeamAbsenceSummary(
  companyIds: number[],
  employeeIds: number[],
): UseMyTeamAbsenceSummaryResult {
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();
  const { createSignal } = useAbortController();

  const [summary, setSummary] = useState<MyTeamAbsenceSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const signal = createSignal();
    setIsLoading(true);

    void (async () => {
      const response = await myTeamAbsenceSummaryService.getSummary(
        {
          companyIds: companyIds.length > 0 ? companyIds : undefined,
          employeeIds: employeeIds.length > 0 ? employeeIds : undefined,
        },
        signal,
      );
      if (response.error?.code === 'ABORTED') return;
      setIsLoading(false);

      if (!response.success || !response.data) {
        showApiError(response, translate('attendance:dashboard.myTeam.absenceSummary.loadError'));
        return;
      }

      setSummary(response.data);
    })();
  }, [companyIds, employeeIds, createSignal, showApiError, translate]);

  return { summary, isLoading };
}

export default useMyTeamAbsenceSummary;
